import type { Theme } from 'types/app/theme';
// <reference types="vite/client" />
const THEME_STORAGE_KEY = 'selectedTheme';
const DEFAULT_THEME_ID = 'default';

const themeFiles = import.meta.glob('../assets/themes/*.json', { eager: true });

// Theme registry - all available themes
const themeRegistry: Map<string, Theme> = new Map();

// Initialize registry from loaded files
function initializeThemeRegistry() {
  for (const path in themeFiles) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mod = themeFiles[path] as any;
    // Handle default export (JSON module)
    const theme = mod.default || mod;

    // Validate theme has an ID
    if (theme && typeof theme === 'object' && 'id' in theme && typeof theme.id === 'string') {
      themeRegistry.set(theme.id, theme as Theme);
    }
  }
}

initializeThemeRegistry();

/**
 * Load a theme from a JSON file
 */
export function loadTheme(themeId: string): Theme | null {
  // Check if theme is already loaded (they are all pre-loaded now)
  if (themeRegistry.has(themeId)) {
    return themeRegistry.get(themeId) || null;
  }

  console.warn(`Theme ${themeId} not found.`);
  return null;
}

/**
 * Get the currently selected theme ID from localStorage
 */
export function getSelectedThemeId(): string {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID;

  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return stored || DEFAULT_THEME_ID;
}

/**
 * Save the selected theme ID to localStorage
 */
export function saveSelectedThemeId(themeId: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THEME_STORAGE_KEY, themeId);
}

/**
 * Apply a theme to the application
 */
export function applyTheme(theme: Theme, isDarkMode: boolean): void {
  if (!theme || !theme.colors) {
    console.error('Invalid theme provided');
    return;
  }

  const colorSet = isDarkMode ? theme.colors.dark : theme.colors.light;

  // Apply Quasar CSS custom properties
  const root = document.documentElement;

  // Primary colors
  root.style.setProperty('--q-primary', colorSet.primary);
  root.style.setProperty('--primary', colorSet.primary);

  // Secondary colors
  root.style.setProperty('--q-secondary', colorSet.secondary);
  root.style.setProperty('--secondary', colorSet.secondary);

  // Positive colors
  root.style.setProperty('--q-positive', colorSet.positive);
  root.style.setProperty('--positive', colorSet.positive);

  // Negative colors
  root.style.setProperty('--q-negative', colorSet.negative);
  root.style.setProperty('--negative', colorSet.negative);

  // Warning colors
  root.style.setProperty('--q-warning', colorSet.warning);
  root.style.setProperty('--warning', colorSet.warning);

  // Important colors
  root.style.setProperty('--q-important', colorSet.important);
  root.style.setProperty('--important', colorSet.important);

  // Background colors (main page backgrounds)
  root.style.setProperty('--q-dark', colorSet.dark);
  root.style.setProperty('--dark', colorSet.dark);
  root.style.setProperty('--q-light', colorSet.light);
  root.style.setProperty('--light', colorSet.light);

  // Surface colors (for cards, panels, etc.)
  root.style.setProperty('--q-surface', colorSet.surface);
  root.style.setProperty('--surface', colorSet.surface);
  root.style.setProperty('--q-darksurface', colorSet.darksurface);
  root.style.setProperty('--darksurface', colorSet.darksurface);

  // White color
  root.style.setProperty('--q-white', colorSet.white);
  root.style.setProperty('--white', colorSet.white);

  // Deltai color
  root.style.setProperty('--q-deltai', '#218380');
  root.style.setProperty('--deltai', '#218380');

  // Quasar v2 uses CSS custom properties (--q-*), which we've set above
  // These will be automatically picked up by Quasar components
}

/**
 * Get all available theme IDs
 */
export function getAvailableThemeIds(): string[] {
  return Array.from(themeRegistry.keys());
}

/**
 * Load and apply a theme by ID
 */
export function loadAndApplyTheme(themeId: string, isDarkMode: boolean): void {
  const theme = loadTheme(themeId);
  if (theme) {
    applyTheme(theme, isDarkMode);
    saveSelectedThemeId(themeId);
  } else {
    // Fallback to default theme if loading fails
    if (themeId !== DEFAULT_THEME_ID) {
      console.warn(`Failed to load theme ${themeId}, falling back to default`);
      loadAndApplyTheme(DEFAULT_THEME_ID, isDarkMode);
    }
  }
}
